import { useCallback, useState } from 'react';

/**
 * Syncs an uncontrolled state branch with an external owner.
 *
 * Use when another source (e.g. a Base UI primitive) owns the authoritative
 * state but the surrounding component still needs to read it — for example to
 * expose it via context to sibling sub-components. The returned setter both
 * updates internal state and forwards to `onChange`, matching the shape
 * expected by callbacks like `onOpenChange`.
 */
export function useSyncUncontrolledState<T>(
  defaultValue: T,
  onChange?: (newValue: T) => void
): [T, (newValue: T) => void] {
  const [value, setValue] = useState(defaultValue);

  const handleChange = useCallback(
    (newValue: T) => {
      setValue(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  return [value, handleChange];
}
